import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Typography, Tooltip } from "@material-ui/core";
import styled, { keyframes } from "styled-components";
import apiFetch from "../util/fetch-utils";
import WorkspaceHeader from "../common/WorkspaceHeader";
import { generateUrlForWorkspace } from "../Workspace/common";

/* ── Animations ───────────────────────────────────────────── */
const fadeIn = keyframes`from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; }`;

/* ── Page layout ──────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    animation: ${fadeIn} 200ms ease;
`;

const Section = styled.div`
    margin-top: 20px;
`;

/* ── Stat tiles ───────────────────────────────────────────── */
const TilesRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const Tile = styled.div`
    border-radius: 14px;
    background: ${({ $gradient }) => $gradient};
    box-shadow: 0 4px 16px ${({ $shadow }) => $shadow};
    padding: 18px 20px;
    height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
    transition: transform 120ms ease, box-shadow 120ms ease;
    &:hover {
        transform: ${({ $clickable }) => ($clickable ? "translateY(-2px)" : "none")};
    }
`;

const TileLabel = styled(Typography)`
    color: rgba(255,255,255,0.82) !important;
    font-size: 0.7rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.6px !important;
    text-transform: uppercase;
`;

const TileNumber = styled(Typography)`
    color: #fff !important;
    font-weight: 800 !important;
    font-size: 1.9rem !important;
    line-height: 1 !important;
    letter-spacing: -1px !important;
`;

const TileSub = styled(Typography)`
    color: rgba(255,255,255,0.6) !important;
    font-size: 0.7rem !important;
`;

/* ── Filter bar ───────────────────────────────────────────── */
const FilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
`;

const FilterBtn = styled.button`
    appearance: none;
    border: 1px solid ${({ $active }) => $active ? "#6366f1" : "#e5e7eb"};
    background: ${({ $active }) => $active ? "#ede9fe" : "#fff"};
    color: ${({ $active }) => $active ? "#4f46e5" : "#374151"};
    border-radius: 99px;
    padding: 5px 14px;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 100ms ease;
    &:hover { border-color: #6366f1; }
`;

/* ── Loading bar ──────────────────────────────────────────── */
const ProgressWrap = styled.div`
    background: #f0f0f0;
    border-radius: 99px;
    height: 6px;
    overflow: hidden;
    margin: 12px 0;
`;

const ProgressFill = styled.div`
    height: 100%;
    border-radius: 99px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6);
    width: ${({ $pct }) => $pct}%;
    transition: width 300ms ease;
`;

/* ── Table ────────────────────────────────────────────────── */
const TableWrap = styled.div`
    border-radius: 14px;
    border: 1px solid #f0f0f0;
    overflow: hidden;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    background: #fff;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
`;

const THead = styled.thead`
    background: #fafafa;
    border-bottom: 1px solid #f0f0f0;
`;

const Th = styled.th`
    padding: 10px 14px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: left;
    white-space: nowrap;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #f9fafb;
    transition: background 100ms ease;
    &:last-child { border-bottom: none; }
    &:hover { background: #f8f7ff; }
`;

const Td = styled.td`
    padding: 10px 14px;
    font-size: 0.85rem;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
`;

const UrlLink = styled.a`
    color: #4f46e5;
    text-decoration: none;
    font-weight: 500;
    &:hover { text-decoration: underline; }
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
    background: ${({ $bg }) => $bg || "#f3f4f6"};
    color: ${({ $color }) => $color || "#374151"};
`;

const EmptyMsg = styled.div`
    padding: 40px;
    text-align: center;
    color: #9ca3af;
    font-size: 0.9rem;
`;

/* ── Classification helpers ───────────────────────────────── */
function classify(row) {
    const lastCrawl = row.lastCrawl || {};
    const sitemapEntries = lastCrawl.inboundLinks?.internal?.SITEMAP || [];
    const inSitemap = sitemapEntries.length > 0;
    const inboundAhref = lastCrawl.inboundLinksCount?.internal?.ahref || 0;

    if (!inSitemap && inboundAhref === 0) return "orphan";
    if (inSitemap && inboundAhref === 0)  return "sitemap-only";
    if (!inSitemap && inboundAhref > 0)   return "not-in-sitemap";
    return "connected";
}

const CATEGORIES = {
    all:           { label: "All pages",           gradient: "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)", shadow: "rgba(37,99,235,.25)",   sub: "fetched" },
    orphan:        { label: "Complete orphans",     gradient: "linear-gradient(135deg, #f87171 0%, #e11d48 100%)", shadow: "rgba(225,29,72,.25)",   sub: "no sitemap · no inbound links" },
    "sitemap-only":{ label: "Sitemap only",         gradient: "linear-gradient(135deg, #fbbf24 0%, #d97706 100%)", shadow: "rgba(217,119,6,.25)",   sub: "in sitemap · no inbound links" },
    "not-in-sitemap":{ label: "Missing from sitemap", gradient: "linear-gradient(135deg, #c084fc 0%, #7c3aed 100%)", shadow: "rgba(124,58,237,.25)", sub: "linked · not in sitemap" },
    connected:     { label: "Well connected",       gradient: "linear-gradient(135deg, #34d399 0%, #059669 100%)", shadow: "rgba(5,150,105,.25)",   sub: "in sitemap · has inbound links" },
};

function statusStyle(code) {
    if (!code) return { bg: "#f3f4f6", color: "#6b7280" };
    if (code < 300) return { bg: "#dcfce7", color: "#15803d" };
    if (code < 400) return { bg: "#dbeafe", color: "#1d4ed8" };
    if (code < 500) return { bg: "#fee2e2", color: "#dc2626" };
    return { bg: "#fce7f3", color: "#9d174d" };
}

function extractStatus(row) {
    const chain = row.currentValue?.redirectChainElements;
    if (!chain || chain.length === 0) return null;
    const last = chain[chain.length - 1].httpStatus;
    if (last >= 400) return last;
    if (chain.length > 1) return chain[chain.length - 2].httpStatus;
    return last;
}

/* ── Data fetching ────────────────────────────────────────── */
async function fetchAllPages(workspaceNumber, onProgress) {
    const PAGE_SIZE = 200;
    let page = 0;
    let totalPages = 1;
    const all = [];

    while (page < totalPages) {
        const qs = new URLSearchParams({ workspaceNumber, size: PAGE_SIZE, page });
        const res = await apiFetch(`/api/monitored-uris/search/searchMonitoredUri?${qs}`);
        const json = await res.json();
        const items = json._embedded?.monitoredUris || [];
        all.push(...items);
        totalPages = json.page?.totalPages || 1;
        page++;
        onProgress(Math.round((page / totalPages) * 100));
    }

    return all;
}

/* ── Component ────────────────────────────────────────────── */
export default function SitemapPage() {
    const { workspaceNumber } = useParams();
    const ws = useSelector((s) => s.workspaces?.current);
    const wsUrl = generateUrlForWorkspace(workspaceNumber);

    const [rows, setRows]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [progress, setProgress] = useState(0);
    const [activeFilter, setActiveFilter] = useState("all");
    const [search, setSearch]     = useState("");

    useEffect(() => {
        setLoading(true);
        setProgress(0);
        setRows([]);
        fetchAllPages(workspaceNumber, setProgress)
            .then(all => { setRows(all); setLoading(false); })
            .catch(() => setLoading(false));
    }, [workspaceNumber]);

    const classified = useMemo(() => rows.map(r => ({ ...r, _cat: classify(r) })), [rows]);

    const counts = useMemo(() => ({
        all:            classified.length,
        orphan:         classified.filter(r => r._cat === "orphan").length,
        "sitemap-only": classified.filter(r => r._cat === "sitemap-only").length,
        "not-in-sitemap": classified.filter(r => r._cat === "not-in-sitemap").length,
        connected:      classified.filter(r => r._cat === "connected").length,
    }), [classified]);

    const visible = useMemo(() => {
        let src = activeFilter === "all" ? classified : classified.filter(r => r._cat === activeFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            src = src.filter(r => r.uri?.toLowerCase().includes(q));
        }
        return src;
    }, [classified, activeFilter, search]);

    const tiles = ["all", "orphan", "sitemap-only", "not-in-sitemap", "connected"];

    return (
        <Page>
            {ws && <WorkspaceHeader name={ws.name} url={ws.websiteUrl} countryCode={ws.countryCode} />}

            {/* ── Summary tiles ── */}
            <Section>
                <TilesRow>
                    {tiles.map(key => {
                        const cfg = CATEGORIES[key];
                        return (
                            <Tooltip key={key} title={cfg.sub} arrow>
                                <Tile
                                    $gradient={cfg.gradient}
                                    $shadow={cfg.shadow}
                                    $clickable
                                    onClick={() => setActiveFilter(key)}
                                    style={{ outline: activeFilter === key ? "3px solid rgba(255,255,255,0.6)" : "none", outlineOffset: "2px" }}
                                >
                                    <TileLabel>{cfg.label}</TileLabel>
                                    <TileNumber>{loading ? "—" : counts[key].toLocaleString()}</TileNumber>
                                    <TileSub>{cfg.sub}</TileSub>
                                </Tile>
                            </Tooltip>
                        );
                    })}
                </TilesRow>
            </Section>

            {/* ── Progress bar while loading ── */}
            {loading && (
                <Section>
                    <Typography style={{ fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>
                        Fetching all pages… {progress}%
                    </Typography>
                    <ProgressWrap>
                        <ProgressFill $pct={progress} />
                    </ProgressWrap>
                </Section>
            )}

            {/* ── Table ── */}
            {!loading && (
                <Section>
                    <FilterBar>
                        {tiles.map(key => (
                            <FilterBtn
                                key={key}
                                $active={activeFilter === key}
                                onClick={() => setActiveFilter(key)}
                            >
                                {CATEGORIES[key].label} ({counts[key].toLocaleString()})
                            </FilterBtn>
                        ))}
                        <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Filter by URL…"
                            style={{
                                marginLeft: "auto",
                                border: "1px solid #e5e7eb",
                                borderRadius: 8,
                                padding: "5px 12px",
                                fontSize: "0.82rem",
                                outline: "none",
                                minWidth: 200,
                            }}
                        />
                    </FilterBar>

                    <TableWrap>
                        <StyledTable>
                            <THead>
                                <tr>
                                    <Th style={{ width: "55%" }}>URL</Th>
                                    <Th style={{ width: "10%" }}>Status</Th>
                                    <Th style={{ width: "14%" }}>Sitemap</Th>
                                    <Th style={{ width: "10%" }}>Inbound links</Th>
                                    <Th style={{ width: "11%" }}>Category</Th>
                                </tr>
                            </THead>
                            <tbody>
                                {visible.length === 0 && (
                                    <tr><td colSpan={5}><EmptyMsg>No pages match this filter.</EmptyMsg></td></tr>
                                )}
                                {visible.map(row => {
                                    const lastCrawl  = row.lastCrawl || {};
                                    const inSitemap  = (lastCrawl.inboundLinks?.internal?.SITEMAP?.length || 0) > 0;
                                    const inbound    = lastCrawl.inboundLinksCount?.internal?.ahref || 0;
                                    const status     = extractStatus(row);
                                    const { bg, color } = statusStyle(status);
                                    const catCfg = {
                                        orphan:           { bg: "#fee2e2", color: "#dc2626", label: "Orphan" },
                                        "sitemap-only":   { bg: "#fef3c7", color: "#d97706", label: "Sitemap only" },
                                        "not-in-sitemap": { bg: "#ede9fe", color: "#7c3aed", label: "Not in sitemap" },
                                        connected:        { bg: "#dcfce7", color: "#15803d", label: "Connected" },
                                    }[row._cat] || {};

                                    return (
                                        <Tr key={row.id}>
                                            <Td>
                                                <Tooltip title={row.uri} placement="top-start">
                                                    <UrlLink href={`/#/monitored-uris/${row.id}`}>{row.uri}</UrlLink>
                                                </Tooltip>
                                            </Td>
                                            <Td>
                                                {status ? <Badge $bg={bg} $color={color}>{status}</Badge> : <span style={{ color: "#d1d5db" }}>—</span>}
                                            </Td>
                                            <Td>
                                                <Badge $bg={inSitemap ? "#dcfce7" : "#fee2e2"} $color={inSitemap ? "#15803d" : "#dc2626"}>
                                                    {inSitemap ? "✓ Yes" : "✗ No"}
                                                </Badge>
                                            </Td>
                                            <Td style={{ color: inbound === 0 ? "#dc2626" : "#374151", fontWeight: inbound === 0 ? 700 : 400 }}>
                                                {inbound.toLocaleString()}
                                            </Td>
                                            <Td>
                                                <Badge $bg={catCfg.bg} $color={catCfg.color}>{catCfg.label}</Badge>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </tbody>
                        </StyledTable>
                    </TableWrap>
                </Section>
            )}
        </Page>
    );
}
